import { Link } from "react-router-dom";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import { useHasInternalNewsquawkUserRole } from "@/hooks/useHasInternalNewsquawkUserRole";
import { checklistTasks } from "./checklistTasks";
import { useOnboardingStore } from "../useOnboardingStore";

interface ChecklistPanelProps {
  onNavigate: () => void;
}

/** Task list inside the getting-started popover. */
export default function ChecklistPanel({ onNavigate }: ChecklistPanelProps) {
  const tasks = useOnboardingStore((s) => s.state?.checklist?.tasks ?? {});
  const markTaskDone = useOnboardingStore((s) => s.markTaskDone);
  const dismissChecklist = useOnboardingStore((s) => s.dismissChecklist);

  const doneCount = checklistTasks.filter((t) => tasks[t.id]).length;

  // Guides are internal-only while the content is written, and /guides
  // redirects for everyone else. A task keeps its title and blurb — and its
  // place in the progress count — but does not offer a CTA that bounces.
  const isInternalUser = useHasInternalNewsquawkUserRole();
  const hiddenHref = (href: string) =>
    !isInternalUser && href.startsWith("/guides");

  return (
    <div className="w-80 rounded-lg border border-gray-700 bg-gray-800 shadow-xl">
      <div className="border-b border-gray-700 px-4 py-3">
        <div className="text-sm font-semibold text-gray-100">
          Getting started
        </div>
        <div className="mt-0.5 text-xs text-gray-400">
          {doneCount} of {checklistTasks.length} done — each one makes the
          platform work harder for you.
        </div>
      </div>

      <ul className="max-h-80 overflow-y-auto p-2">
        {checklistTasks.map((task) => {
          const done = !!tasks[task.id];
          return (
            <li
              key={task.id}
              className="flex items-start gap-2.5 rounded-md px-2 py-2 hover:bg-gray-700/40"
            >
              <CheckCircleIcon
                className={`mt-0.5 h-5 w-5 shrink-0 ${
                  done ? "text-green-500" : "text-gray-600"
                }`}
                aria-hidden="true"
              />
              <div className="min-w-0 flex-1">
                <div
                  className={`text-sm font-medium ${
                    done ? "text-gray-500 line-through" : "text-gray-200"
                  }`}
                >
                  {task.title}
                </div>
                {!done && (
                  <>
                    <p className="mt-0.5 text-xs text-gray-400">{task.blurb}</p>
                    {task.externalHref ? (
                      <a
                        href={task.externalHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => {
                          // Store CTAs self-complete — we can't observe the
                          // install itself.
                          markTaskDone(task.id);
                        }}
                        className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-squawk-blue hover:underline"
                      >
                        {task.ctaLabel}
                        <ArrowTopRightOnSquareIcon className="h-3 w-3" />
                      </a>
                    ) : task.href && !hiddenHref(task.href) ? (
                      <Link
                        to={task.href}
                        onClick={onNavigate}
                        className="mt-1 inline-block text-xs font-medium text-squawk-blue hover:underline"
                      >
                        {task.ctaLabel}
                      </Link>
                    ) : null}
                  </>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      <div className="border-t border-gray-700 px-4 py-2 text-right">
        <button
          type="button"
          onClick={dismissChecklist}
          className="text-xs text-gray-500 hover:text-gray-300 transition-colors bg-transparent border-none cursor-pointer"
        >
          Dismiss checklist
        </button>
      </div>
    </div>
  );
}
